// src/components/CreatePage.ts
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { API_ENDPOINT, CDN_CONTENT_ENDPOINT } from '../constants';
import { estimateCredits, createCheckoutSession, getGeneratedContent, createDiffsonaFromDescription } from '../client/ApiClient';
import AppAnalytics from '../client/AnalyticsClient';
import { femaleNameRandom } from '../utils/name';
import { consume } from '@lit/context';
import { AuthContext, AuthState } from './AuthProvider';
import { decodeState, encodeState, DEMO_LS_KEY, savePostAuthIntent, takePostAuthIntent } from '../utils/post-auth';
import { DiffsonaPrompts } from './CurrentDiffsona';
import './CollapsibleSection';
import './InfoTooltip';

type Choice = { title: string; prompt: string };
export type PromptPayload = {
  prompt: {
    style: string;
    clothing: string;
    pose: string;
    set: string;
    framing: string;
    viewpoint: string;
  }
  resources?: Array<{ id: string; strength: number }>;
};

type Resource = {
  id: string;
  display_name: string;
  description: string;
  sample_image: string;
  strength: number;
};

export type JobStatus = 'queued' | 'running' | 'done' | 'error' | 'timeout';
export type Job = {
  jobId: string;
  status: JobStatus;
  startedAt: number;
  elapsedSec: number;
  imageUrl?: string;
  summary: string;
  prompt: PromptPayload;
  error?: string;
  isAnonymous?: boolean; // Track if this is an anonymous generation
};

type FormState = {
  top: string;
  bottom: string;
  pose: string;
  set: string;
  extra: string;
  framing: string;
  viewpoint: string;
  resources?: Array<{ id: string; strength: number }>;
};

const emptyForm: FormState = {
  top: '', bottom: '', pose: '', set: '', extra: '',
  framing: '', viewpoint: '',
};

type FormStateWithDemoImage = FormState & {
  demoImages: string[];
};

type FormFieldKey = 'top' | 'bottom' | 'pose' | 'set' | 'framing' | 'viewpoint' | 'extra';

const FIELD_INFO: Record<FormFieldKey, { description: string; examples: string[] }> = {
  top: {
    description: 'Upper body clothing or style',
    examples: ['white t-shirt', 'black leather jacket', 'floral sundress']
  },
  bottom: {
    description: 'Lower body clothing',
    examples: ['blue jeans', 'black skirt', 'yoga pants', 'shorts']
  },
  pose: {
    description: 'Body position and posture',
    examples: ['standing', 'sitting cross-legged', 'leaning against wall', 'arms crossed']
  },
  set: {
    description: 'Location and environment',
    examples: ['urban street', 'cozy cafe', 'beach sunset', 'modern office']
  },
  framing: {
    description: 'Camera shot distance',
    examples: ['close-up', 'medium shot', 'full body', 'waist up']
  },
  viewpoint: {
    description: 'Camera angle perspective',
    examples: ['eye level', 'low angle', 'high angle', 'over shoulder']
  },
  extra: {
    description: 'Additional scene details',
    examples: ['golden hour lighting', 'rain effect', 'bokeh background', 'dramatic shadows']
  }
};

declare global { interface Window { Stripe?: any } }

const DEFAULT_FRAMING = 'medium shot';
const DEFAULT_VIEWPOINT = 'eye level';
const DEFAULT_SLUG = '_default_';
const DEMO_JOBS_LS_KEY = 'diffsona:demo:jobs';

const demoName = femaleNameRandom();

type PresetType = 'r' | 'l' | 'a' | 'p' | 'pa'

const demoPresetValues: Record<PresetType, DiffsonaPrompts> = {
  'r': { // roselie
    age_prompt: '20 year old woman',
    hair_prompt: 'long brown hair, subtle curly hair',
    face_prompt: 'pudgy face',
    body_prompt: 'small breasts, waist, hips, thick thighs',
    ethnicity_prompt: 'mixed race, latina, white girl',
    base_prompt: '',
    name: '',
  },
  'l': { // laury
    age_prompt: '21 year old pretty',
    hair_prompt: 'long brown hair',
    ethnicity_prompt: 'european',
    face_prompt: 'light makeup, pretty face, lips, eyes',
    body_prompt: 'small breasts, waist, long legs',
    base_prompt: '',
    name: '',
  },
  'a': { // awa
    age_prompt: '25 year old woman',
    hair_prompt: 'blonde hair, ponytail',
    ethnicity_prompt: '',
    face_prompt: 'minimal makeup, natural look',
    body_prompt: 'relaxed posture, comfortable stance',
    base_prompt: '',
    name: '',
  },
  'p': { // generic petite
    age_prompt: '26 year old professional woman',
    hair_prompt: 'neat hairstyle, polished',
    ethnicity_prompt: '',
    face_prompt: 'professional makeup, confident expression',
    body_prompt: 'confident posture, professional stance',
    base_prompt: '',
    name: '',
  },
  'pa': { // generic pawg
    age_prompt: '26 year old professional woman',
    hair_prompt: 'neat hairstyle, polished',
    ethnicity_prompt: '',
    face_prompt: 'professional makeup, confident expression',
    body_prompt: 'confident posture, professional stance',
    base_prompt: '',
    name: '',
  },
};

// Helper function to randomly select a demo image
const getRandomDemoImage = (images: string[]): string | null => {
  if (images.length === 0) return null;
  return images[Math.floor(Math.random() * images.length)];
};

// Get demo styling preset including outfit, pose, set, resources, and demo images
const getDemoStylingPreset = (demoPreset: PresetType): FormStateWithDemoImage | null => {
  if (!demoPreset) return null;

  const stylingPresets: Record<PresetType, FormStateWithDemoImage> = {
    'r': { // roselie
      top: 'form-fitting ribbed crop top',
      bottom: 'high-waisted skinny jeans',
      pose: 'three-quarter pose, contrapposto',
      set: 'clean studio backdrop, softbox key, soft rim light',
      extra: 'editorial polish, healthy glow',
      resources: [{ id: 'roselie', strength: 1.0 }],
      framing: '',
      viewpoint: '',
      demoImages: [] // No images available for roselie
    },
    'l': { // laury
      top: 'tailored single-breasted blazer, sharp lapels',
      bottom: 'high-rise tailored trousers, straight leg',
      pose: 'hands on hips, shoulders back, confident',
      set: 'daylight loft, large window light, gentle shadow',
      extra: 'editorial polish, styled hair, subtle gloss',
      resources: [{ id: 'laury', strength: 1.0 }],
      framing: '',
      viewpoint: '',
      demoImages: [
        '/static/images/diffsona-demo/l/0.webp',
        '/static/images/diffsona-demo/l/1.webp',
        '/static/images/diffsona-demo/l/2.webp',
        '/static/images/diffsona-demo/l/3.webp',
        '/static/images/diffsona-demo/l/4.webp',
        '/static/images/diffsona-demo/l/5.webp',
        '/static/images/diffsona-demo/l/6.webp'
      ]
    },
    'a': { // awa
      top: 'performance sports bra, minimal branding',
      bottom: 'high-compression leggings',
      pose: 'runway walk turn, mid-stride, chin slightly up',
      set: 'runway catwalk, spotlights, shallow depth',
      extra: 'defined athletic tone, healthy glow',
      resources: [{ id: 'awa', strength: 1.0 }],
      framing: '',
      viewpoint: '',
      demoImages: [
        '/static/images/diffsona-demo/a/0.webp',
        '/static/images/diffsona-demo/a/1.webp',
        '/static/images/diffsona-demo/a/2.webp'
      ]
    },
    'p': { // generic petite
      top: 'oversized vintage denim jacket',
      bottom: 'pleated mini skirt, mid-thigh',
      pose: 'seated, leaning forward, elbows on knees',
      set: 'seamless paper backdrop, even light',
      extra: 'minimal makeup, natural skin texture',
      resources: [],
      framing: '',
      viewpoint: '',
      demoImages: [
        '/static/images/diffsona-demo/p/0.webp',
        '/static/images/diffsona-demo/p/1.webp',
        '/static/images/diffsona-demo/p/2.webp',
        '/static/images/diffsona-demo/p/3.webp',
        '/static/images/diffsona-demo/p/4.webp'
      ]
    },
    'pa': { // generic pawg
      top: 'form-fitting ribbed crop top',
      bottom: 'high-waisted skinny jeans',
      pose: 'looking back',
      set: 'clean studio backdrop, softbox key, soft rim light',
      extra: 'e-commerce clean look, front-on, garment clarity',
      resources: [],
      framing: '',
      viewpoint: '',
      demoImages: [
        '/static/images/diffsona-demo/pa/0.webp',
        '/static/images/diffsona-demo/pa/1.webp',
        '/static/images/diffsona-demo/pa/2.webp',
        '/static/images/diffsona-demo/pa/3.webp'
      ]
    },
  };

  const preset = stylingPresets[demoPreset];
  if (!preset) return null;

  return {
    ...preset,
    selectedDemoImage: getRandomDemoImage(preset.demoImages)
  };
}

@customElement('diffsona-create-page')
export class DiffsonaCreatePage extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: String }) slug = '';

  @property({ type: Boolean }) demo = false;
  @property({ type: Boolean }) prefillOnLoad = false;
  @property({ type: String, attribute: 'demo-preset' }) demoPreset: PresetType = 'p';
  @property({ type: Object }) diffsonaPrompts: DiffsonaPrompts | null = null;
  @property({ type: Array }) initialJobs: Job[] = [];

  @state() private demoDiffsonaPrompts: DiffsonaPrompts | null = null;
  @state() private demoStyling: ReturnType<typeof getDemoStylingPreset> | null = null;
  @state() private randomDemoStyling: FormStateWithDemoImage | null = null;

  /** Optional curated demo images (CDN or absolute URLs) */
  @property({ type: Array }) demoImages: string[] = [];

  @consume({ context: AuthContext, subscribe: true })
  @state() authState!: AuthState;

  @state() private activeTab: 'images' | 'scenarios' = 'images';
  @state() private jobs: Job[] = [];
  @state() private creatingJob = false;
  @state() private error: string | null = null;

  @state() private estCost: number | null = null;
  @state() private canAfford: boolean = true;
  @state() private balance: number | null = null;

  @state() private form: FormState = { ...emptyForm };
  private get LS_KEY() { return `diffsona:create:${this.slug || 'global'}`; }

  @state() private nsfwBlock: { code: string; message: string } | null = null;

  // Character selector state
  @state() private availableResources: Resource[] = [];
  @state() private selectedResources: Map<string, number> = new Map(); // resourceId -> strength
  @state() private resourcesLoading = false;

  // Top up modal
  @state() private topUpOpen = false;
  @state() private redirecting = false;
  private balancePoll?: number;
  private postAuthHandled = false;

  private intervalId: number | null = null;

  // Presets
  private presets = {
    top: <Choice[]>[
      { title: 'Fitted crop top', prompt: 'form-fitting ribbed crop top' },
      { title: 'Oversized denim jacket', prompt: 'oversized vintage denim jacket' },
      { title: 'Tailored blazer', prompt: 'tailored single-breasted blazer, sharp lapels' },
      { title: 'Sports bra', prompt: 'performance sports bra, minimal branding' },
    ],
    bottom: <Choice[]>[
      { title: 'High-waist jeans', prompt: 'high-waisted skinny jeans' },
      { title: 'Pleated mini skirt', prompt: 'pleated mini skirt, mid-thigh' },
      { title: 'Tailored trousers', prompt: 'high-rise tailored trousers, straight leg' },
      { title: 'Compression leggings', prompt: 'high-compression leggings' },
    ],
    pose: <Choice[]>[
      { title: 'Looking Back', prompt: 'looking back' },
      { title: 'Three-quarter', prompt: 'three-quarter pose, contrapposto, weight on back leg' },
      { title: 'Hands on hips', prompt: 'hands on hips, shoulders back, confident' },
      { title: 'Runway turn', prompt: 'runway walk turn, mid-stride, chin slightly up' },
      { title: 'Seated lean', prompt: 'seated, leaning forward, elbows on knees' },
    ],
    set: <Choice[]>[
      { title: 'Softbox studio', prompt: 'clean studio backdrop, softbox key, soft rim light' },
      { title: 'Seamless paper', prompt: 'seamless paper backdrop, even light' },
      { title: 'Runway lights', prompt: 'runway catwalk, spotlights, shallow depth' },
      { title: 'Loft window', prompt: 'daylight loft, large window light, gentle shadow' },
    ],
    extra: <Choice[]>[
      { title: 'Editorial polish', prompt: 'editorial polish, styled hair, subtle gloss' },
      { title: 'Athletic tone', prompt: 'defined athletic tone, healthy glow' },
      { title: 'Minimal makeup', prompt: 'minimal makeup, natural skin texture' },
      { title: 'E-commerce clean', prompt: 'e-commerce clean look, front-on, garment clarity' },
    ],
    framing: <Choice[]>[
      { title: 'Extreme close-up', prompt: 'extreme close-up' },
      { title: 'Close-up', prompt: 'close-up' },
      { title: 'Medium shot', prompt: 'medium shot' },
      { title: 'Medium long shot', prompt: 'medium long shot' },
      { title: 'Full shot', prompt: 'full shot' },
    ],
    viewpoint: <Choice[]>[
      { title: 'Back', prompt: 'back view' },
      { title: 'Front', prompt: 'front view' },
      { title: 'Three-quarter view', prompt: 'three-quarter view' },
      { title: 'Profile / side', prompt: 'profile side view' },
      { title: 'High angle', prompt: 'high angle' },
      { title: 'Low angle', prompt: 'low angle' },
      { title: 'Eye level', prompt: 'eye level' },
    ],
  };

  // Save anonymous demo jobs to localStorage
  private saveDemoJobs() {
    try {
      const anonymousJobs = this.jobs.filter(j => j.isAnonymous && j.status === 'done' && j.imageUrl);
      if (anonymousJobs.length > 0) {
        localStorage.setItem(DEMO_JOBS_LS_KEY, JSON.stringify(anonymousJobs));
      }
    } catch (e) {
      console.warn('Failed to save demo jobs:', e);
    }
  }

  // Load anonymous demo jobs from localStorage
  private loadDemoJobs() {
    try {
      const raw = localStorage.getItem(DEMO_JOBS_LS_KEY);
      if (raw) {
        const demoJobs = JSON.parse(raw) as Job[];
        // Only load jobs that are completed and have images
        const validJobs = demoJobs.filter(j => j.status === 'done' && j.imageUrl && j.isAnonymous);
        if (validJobs.length > 0) {
          // Merge with existing jobs (demo jobs go at the end)
          this.jobs = [...this.jobs, ...validJobs];
        }
      }
    } catch (e) {
      console.warn('Failed to load demo jobs:', e);
    }
  }

  // Clear demo jobs from localStorage (useful after login)
  private clearDemoJobs() {
    try {
      localStorage.removeItem(DEMO_JOBS_LS_KEY);
    } catch (e) {
      console.warn('Failed to clear demo jobs:', e);
    }
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.demoDiffsonaPrompts = this.getDemoPrompts();
    this.demoStyling = getDemoStylingPreset(this.demoPreset);

    // Listen for changes from CurrentDiffsona component
    this.addEventListener('diffsona-prompts-changed', (e: Event) => {
      const customEvent = e as CustomEvent<DiffsonaPrompts>;
      if (customEvent.detail && this.demo) {
        this.demoDiffsonaPrompts = customEvent.detail;
        this.requestUpdate();
      }
    });

    // Initialize random demo styling once for consistent demo image selection
    if (!this.demoPreset) {
      const availablePresets: PresetType[] = ['r', 'l', 'a', 'pa']; // Exclude 'p'
      const presetsWithImages = availablePresets.filter(preset => {
        const styling = getDemoStylingPreset(preset);
        return styling && styling.demoImages.length > 0;
      });

      if (presetsWithImages.length > 0) {
        const randomPreset = presetsWithImages[Math.floor(Math.random() * presetsWithImages.length)];
        this.randomDemoStyling = getDemoStylingPreset(randomPreset);
        (['top', 'bottom', 'pose', 'set', 'extra', 'framing', 'viewpoint'] as (keyof FormState)[]).forEach((k) => {
          if (this.randomDemoStyling) {
            if (!this.form[k]) {
              this.form = { ...this.form, [k]: this.randomDemoStyling[k] };
            }
          }
        });
      }
    }

    // Normalize empty slug to DEFAULT_SLUG so downstream code stays happy
    if (!this.slug) {
      this.slug = DEFAULT_SLUG;
    }

    // Initialize jobs from initialJobs prop if provided
    if (this.initialJobs && this.initialJobs.length > 0) {
      this.jobs = [...this.initialJobs];
    }

    // Load demo jobs from localStorage if available
    this.loadDemoJobs();

    AppAnalytics.getPlausible()?.trackEvent('create_page_view', {
      props: { slug: this.slug || '' },
    });

    // Try to run post-auth action if user just logged in
    // This handles the case where the page is loaded fresh after login
    // with auth state already populated
    console.log('[CreatePage] connectedCallback - checking for post-auth intent');
    setTimeout(() => this.tryRunPostAuth(), 100);

    // Restore form state
    try {
      const raw = localStorage.getItem(this.LS_KEY);
      if (raw) {
        this.form = { ...emptyForm, ...(JSON.parse(raw) as FormState) };
        // Restore resources from form state
        if (this.form.resources && Array.isArray(this.form.resources)) {
          const resourcesMap = new Map<string, number>();
          this.form.resources.forEach((res) => {
            if (res.id && typeof res.strength === 'number') {
              resourcesMap.set(res.id, res.strength);
            }
          });
          this.selectedResources = resourcesMap;
        }
      }
    } catch { }

    // Load available resources
    this.loadAvailableResources();

    // elapsed timer for job skeletons
    this.intervalId = window.setInterval(() => {
      const now = Date.now();
      let changed = false;
      this.jobs = this.jobs.map(j => {
        if (j.status === 'queued' || j.status === 'running') {
          const elapsedSec = Math.max(0, Math.floor((now - j.startedAt) / 1000));
          if (elapsedSec !== j.elapsedSec) {
            changed = true;
            return { ...j, elapsedSec };
          }
        }
        return j;
      });
      if (changed) this.requestUpdate();
    }, 500);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.intervalId) window.clearInterval(this.intervalId);
    if (this.balancePoll) window.clearInterval(this.balancePoll);
    // Unlock body scroll when component disconnects
    document.body.style.overflow = '';
  }

  private tryRunPostAuth() {
    const rawIntent = sessionStorage.getItem('diffsona:postAuth:intent');
    console.log('[CreatePage] tryRunPostAuth called', {
      postAuthHandled: this.postAuthHandled,
      hasUser: !!this.authState?.user,
      rawIntentInStorage: rawIntent
    });

    if (this.postAuthHandled) return;
    if (!this.authState?.user) return;

    const intent = takePostAuthIntent<{ action: string; credits: number }>();
    console.log('[CreatePage] Post-auth intent retrieved:', intent);

    if (intent?.action === 'topup-credits' && intent.credits) {
      this.postAuthHandled = true;
      console.log('[CreatePage] Resuming top-up purchase for', intent.credits, 'credits');

      AppAnalytics.getPlausible()?.trackEvent('credits_topup_postauth_resume', {
        props: { credits: intent.credits },
      });
      // Resume the purchase flow (modal stays closed, proceed directly to checkout)
      this.buyCredits(intent.credits);
    }
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);

    // Lock/unlock body scroll when modal opens/closes
    if (changedProperties.has('topUpOpen')) {
      if (this.topUpOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }

    // Handle post-auth purchase resumption
    if (changedProperties.has('authState')) {
      console.log('[CreatePage] Auth state changed, calling tryRunPostAuth');
      this.tryRunPostAuth();
    }
  }

  firstUpdated() {
    // 0) If we have a ?prefill=… param, apply it now (one-shot)
    const sp = new URLSearchParams(window.location.search);
    const prefillParam = sp.get('prefill');
    if (prefillParam) {
      const pf = decodeState<Record<string, any>>(prefillParam) || {};
      (['top', 'bottom', 'pose', 'set', 'extra', 'framing', 'viewpoint'] as const).forEach((k) => {
        const v = (pf[k] || '').trim();
        if (!v) return;
        const el = this.querySelector<any>('#' + k);
        el?.setValue?.(v);
        this.form = { ...this.form, [k]: v };
      });

      // Handle resources prefill
      if (pf.resources && Array.isArray(pf.resources)) {
        const resourcesMap = new Map<string, number>();
        pf.resources.forEach((res: any) => {
          if (res.id && typeof res.strength === 'number') {
            resourcesMap.set(res.id, res.strength);
          }
        });
        this.selectedResources = resourcesMap;
        this.form = { ...this.form, resources: pf.resources };
      }

      try { localStorage.setItem(this.LS_KEY, JSON.stringify(this.form)); } catch { }
    }

    // Apply demo preset styling if available and no prefill was applied
    if (!prefillParam && this.demo && this.demoPreset) {
      const demoStyling = this.demoStyling
      if (demoStyling) {
        (['top', 'bottom', 'pose', 'set', 'extra'] as const).forEach((k) => {
          const v = demoStyling[k];
          if (v) {
            const el = this.querySelector<any>('#' + k);
            el?.setValue?.(v);
            this.form = { ...this.form, [k]: v };
          }
        });

        // Apply demo preset resources
        if (demoStyling.resources && demoStyling.resources.length > 0) {
          const resourcesMap = new Map<string, number>();
          demoStyling.resources.forEach((res) => {
            resourcesMap.set(res.id, res.strength);
          });
          this.selectedResources = resourcesMap;
          this.form = { ...this.form, resources: demoStyling.resources };
        }

        try { localStorage.setItem(this.LS_KEY, JSON.stringify(this.form)); } catch { }
      }
    }

    // your existing hydration
    ['top', 'bottom', 'pose', 'set', 'extra', 'framing', 'viewpoint'].forEach(id => {
      const v = (this.form as any)[id] || '';
      const el = this.querySelector<any>(`#${id}`);
      if (el?.setValue && v) el.setValue(v);
    });

    // refresh estimate after we possibly wrote prefill
    this.refreshEstimate();

    // Persist & re-estimate on change (unchanged)
    this.addEventListener('change', (e: Event) => {
      const t = e.target as HTMLElement & { value?: string };
      const id = (t as any)?.id;
      if (id && ['top', 'bottom', 'pose', 'set', 'extra', 'framing', 'viewpoint'].includes(id)) {
        const val = (t as any).value ?? '';
        this.form = { ...this.form, [id]: String(val) } as any;
        try { localStorage.setItem(this.LS_KEY, JSON.stringify(this.form)); } catch { }
        this.refreshEstimate();
      }
    });

    // 1) If ?autogen=1, enqueue automatically after estimate is ready
    if (sp.get('autogen') === '1') {
      // small delay to ensure estimate chips are updated; then fire
      setTimeout(() => this.enqueueJob(true), 300);
    }
  }

  // === Demo helpers (no-API path) ===
  private async refreshEstimate() {
    if (this.demo) {
      // Lightweight synthetic estimate so the chip UI can show a value
      this.estCost = 0;
      this.balance = 200;
      this.canAfford = true;
      return;
    }

    try {
      const { cost, balance, can_afford } = await estimateCredits(this.currentPrompt());
      this.estCost = cost; this.balance = balance; this.canAfford = can_afford;
    } catch { this.estCost = null; this.canAfford = true; }
  }

  private async enqueuePrompt(prompt: PromptPayload) {
    const loggedOut = this.authState?.user === null;

    if (loggedOut) {
      // Use new anonymous endpoint directly without Firebase auth
      AppAnalytics.getPlausible()?.trackEvent('demo_generate_click', {
        props: { clothing: prompt.prompt.clothing, pose: prompt.prompt.pose, set: prompt.prompt.set, style: prompt.prompt.style },
      });

      try {
        // Get demo data from localStorage or use current demoDiffsonaPrompts
        const descPayload = localStorage.getItem(DEMO_LS_KEY);
        const demoData = descPayload ? JSON.parse(descPayload) : {};

        // Merge localStorage data with current demoDiffsonaPrompts (prioritize current state)
        const characterPrompts = this.demoDiffsonaPrompts || demoData;

        const p = this.currentPrompt();

        // Build the complete request with character description fields
        const requestPayload = {
          ...p,
          base: characterPrompts.base_prompt || '',
          age: characterPrompts.age_prompt || '',
          ethnicity: characterPrompts.ethnicity_prompt || '',
          hair: characterPrompts.hair_prompt || '',
          face: characterPrompts.face_prompt || '',
          body: characterPrompts.body_prompt || '',
          bust: characterPrompts.bust_prompt || '',
          waist: characterPrompts.waist_prompt || '',
          hips: characterPrompts.hips_prompt || '',
        };

        const res = await fetch(`${API_ENDPOINT}/generate-content-anonymous`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestPayload),
        });

        if (!res.ok) {
          let body: any = null;
          const resp = await res.text();
          try { body = JSON.parse(resp); } catch { }

          if (res.status === 400 && (body?.error === 'nsfw_explicit_focus' || body?.error === 'nsfw_nudity' || body?.error === 'nsfw_minor')) {
            AppAnalytics.getPlausible()?.trackEvent('create_nsfw_block', { props: { code: body?.error || 'unknown' } });
            this.nsfwBlock = { code: body.error, message: body.message || 'This prompt is too explicit.' };
            return;
          }

          if (res.status === 429 && body?.error === 'rate_limit_exceeded') {
            // Rate limit exceeded - show auth wall
            AppAnalytics.getPlausible()?.trackEvent('create_anonymous_rate_limit');

            const createPrefill = this.currentPrompt();
            savePostAuthIntent({ action: 'create-from-description', payload: demoData, createPrefill });

            this.dispatchEvent(new CustomEvent('auth-required', {
              bubbles: true,
              composed: true,
              detail: {
                source: 'rate-limit',
                message: body.message || 'Thanks for trying out a few generations! Sign in for unlimited access.'
              }
            }));
            return;
          }

          throw new Error(resp || 'Failed to start job');
        }

        const { job_id } = await res.json();
        AppAnalytics.getPlausible()?.trackEvent('create_job_enqueued');

        this.addJob({
          jobId: job_id,
          status: 'queued',
          startedAt: Date.now(),
          elapsedSec: 0,
          summary: this.summarizePrompt(prompt),
          prompt: prompt,
          isAnonymous: true, // Mark as anonymous generation
        });

        this.pollJobStatus(job_id);
        return;

      } catch (error) {
        console.error('Anonymous generation failed:', error);
        AppAnalytics.getPlausible()?.trackEvent('create_job_error');

        // Show auth wall as fallback
        const createPrefill = this.currentPrompt();
        const descPayload = localStorage.getItem(DEMO_LS_KEY);
        savePostAuthIntent({ action: 'create-from-description', payload: descPayload ? JSON.parse(descPayload) : "", createPrefill });

        this.dispatchEvent(new CustomEvent('auth-required', {
          bubbles: true,
          composed: true,
          detail: {
            source: 'demo-generate',
            message: 'Please sign in to generate images.'
          }
        }));
        return;
      }
    }

    AppAnalytics.getPlausible()?.trackEvent('create_generate_click', {
      props: { clothing: prompt.prompt.clothing, pose: prompt.prompt.pose, set: prompt.prompt.set, style: prompt.prompt.style },
    });

    // === Normal (authenticated) path ===
    console.log(`[CREATE] slug=${this.slug} status=starting_request`);
    try {
      // If diffsonaPrompts are provided, merge them into the request (like demo flow)
      let requestBody = prompt;
      if (this.diffsonaPrompts) {
        const characterPrompts = this.diffsonaPrompts;
        requestBody = {
          ...prompt,
          base: characterPrompts.base_prompt || '',
          age: characterPrompts.age_prompt || '',
          ethnicity: characterPrompts.ethnicity_prompt || '',
          hair: characterPrompts.hair_prompt || '',
          face: characterPrompts.face_prompt || '',
          body: characterPrompts.body_prompt || '',
          bust: characterPrompts.bust_prompt || '',
          waist: characterPrompts.waist_prompt || '',
          hips: characterPrompts.hips_prompt || '',
        };
      }

      const res = await fetch(`${API_ENDPOINT}/diffsona/${this.slug}/generate-content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        credentials: 'include',
      });

      console.log(`[CREATE] status_code=${res.status} ok=${res.ok}`);

      if (!res.ok) {
        let body: any = null;
        const resp = await res.text();
        try { body = JSON.parse(resp); } catch { }

        if (res.status === 400 && (body?.error === 'nsfw_explicit_focus' || body?.error === 'nsfw_nudity' || body?.error === 'nsfw_minor')) {
          console.warn(`[CREATE] status=nsfw_blocked code=${body.error}`);
          AppAnalytics.getPlausible()?.trackEvent('create_nsfw_block', { props: { code: body?.error || 'unknown' } });
          this.nsfwBlock = { code: body.error, message: body.message || 'This prompt is too explicit.' };
          return;
        }

        if (res.status === 429 && body?.error === 'rate_limit_exceeded') {
          console.warn(`[CREATE] status=rate_limited`);
          // Rate limit exceeded - show auth wall
          AppAnalytics.getPlausible()?.trackEvent('create_anonymous_rate_limit');

          const createPrefill = this.currentPrompt();
          const descPayload = localStorage.getItem(DEMO_LS_KEY);
          savePostAuthIntent({ action: 'create-from-description', payload: descPayload ? JSON.parse(descPayload) : "", createPrefill });

          this.dispatchEvent(new CustomEvent('auth-required', {
            bubbles: true,
            composed: true,
            detail: {
              source: 'rate-limit',
              message: body.message || 'Thanks for trying out a few generations! Sign in for unlimited access.'
            }
          }));
          return;
        }

        console.error(`[CREATE] status=request_failed response=${resp}`);
        throw new Error(resp || 'Failed to start job');
      }

      const { job_id } = await res.json();
      console.log(`[CREATE] job_id=${job_id} status=enqueued`);
      AppAnalytics.getPlausible()?.trackEvent('create_job_enqueued');

      const startedAt = Date.now();
      const job: Job = { jobId: job_id, status: 'queued', startedAt, elapsedSec: 0, summary: '', prompt };
      this.jobs = [job, ...this.jobs];
      this.pollJob(job.jobId).catch(() => { });
    } catch (e: any) {
      console.error(`[CREATE] status=error message=${e?.message}`);
      this.error = e?.message || 'Failed to queue generation';
      AppAnalytics.getPlausible()?.trackEvent('create_job_enqueue_error', { props: { message: e?.message ?? 'unknown' } });
    }
  }

  private currentPrompt(): PromptPayload {
    const top = this.getField('top');
    const bottom = this.getField('bottom');
    const prompt: PromptPayload = {
      prompt: {
        style: this.getField('extra'),
        clothing: [top, bottom].filter(Boolean).join(', '),
        pose: this.getField('pose'),
        set: this.getField('set'),
        framing: this.getField('framing'),
        viewpoint: this.getField('viewpoint'),
      }
    };

    if (this.selectedResources.size > 0) {
      prompt.resources = Array.from(this.selectedResources.entries()).map(([id, strength]) => ({
        id,
        strength
      }));
    }

    return prompt;
  }

  private getField(id: string): string {
    const el = this.querySelector<HTMLElement & { value?: string; selectedPrompt?: string }>(`#${id}`);
    return (el?.selectedPrompt || (el as any)?.value || '').trim();
  }

  private formatDuration(s: number) {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return m ? `${m}m ${r}s` : `${r}s`;
  }

  private createCoinSplash(e: MouseEvent | TouchEvent) {
    // Get click/touch position
    let x: number, y: number;
    if (e instanceof MouseEvent) {
      x = e.clientX;
      y = e.clientY;
    } else {
      const touch = e.touches[0] || e.changedTouches[0];
      x = touch.clientX;
      y = touch.clientY;
    }

    const particleCount = 16;
    const particles: HTMLElement[] = [];

    for (let i = 0; i < particleCount; i++) {
      // Mix of coins and sparkles (60% coins, 40% sparkles)
      const isSparkle = Math.random() > 0.6;

      let particle: HTMLElement;
      if (isSparkle) {
        particle = document.createElement('div');
        particle.textContent = '✨';
        particle.style.cssText = `
          position: fixed;
          left: ${x}px;
          top: ${y}px;
          font-size: 24px;
          pointer-events: none;
          z-index: 10000;
          transition: none;
        `;
      } else {
        particle = document.createElement('img');
        (particle as HTMLImageElement).src = '/static/images/diffso-coin-64.webp';
        particle.style.cssText = `
          position: fixed;
          left: ${x}px;
          top: ${y}px;
          width: 32px;
          height: 32px;
          pointer-events: none;
          z-index: 10000;
          transition: none;
        `;
      }

      document.body.appendChild(particle);
      particles.push(particle);

      // Random angle and velocity for splash effect
      const angle = (Math.PI * 2 * i) / particleCount;
      const velocity = 150 + Math.random() * 100; // pixels per second
      const vx = Math.cos(angle) * velocity;
      const vy = Math.sin(angle) * velocity - 100; // Initial upward velocity

      const startTime = Date.now();
      const gravity = 800; // pixels per second^2

      const animate = () => {
        const elapsed = (Date.now() - startTime) / 1000; // seconds

        if (elapsed >= 2) {
          particle.remove();
          return;
        }

        // Physics: position = initial + velocity*time + 0.5*acceleration*time^2
        const newX = x + vx * elapsed;
        const newY = y + vy * elapsed + 0.5 * gravity * elapsed * elapsed;

        // Rotation for visual appeal (sparkles rotate faster)
        const rotationSpeed = isSparkle ? 4 : 2; // rotations per second
        const rotation = elapsed * 360 * rotationSpeed;

        // Fade out in last 0.5 seconds
        const opacity = elapsed > 1.5 ? 1 - (elapsed - 1.5) * 2 : 1;

        particle.style.transform = `translate(-50%, -50%) rotate(${rotation}deg)`;
        particle.style.left = `${newX}px`;
        particle.style.top = `${newY}px`;
        particle.style.opacity = `${opacity}`;

        requestAnimationFrame(animate);
      };

      requestAnimationFrame(animate);
    }

    // Cleanup after 2.5 seconds (safety)
    setTimeout(() => {
      particles.forEach(particle => particle.remove());
    }, 2500);
  }

  private async loadAvailableResources() {
    this.resourcesLoading = true;
    try {
      const response = await fetch(`${API_ENDPOINT}/resources`);
      if (response.ok) {
        const data = await response.json();
        this.availableResources = data.resources || [];
      }
    } catch (e) {
      console.error('Failed to load resources:', e);
      // Fallback resources in case API fails
      this.availableResources = [];
    } finally {
      this.resourcesLoading = false;
    }
  }

  private toggleResource = (resourceId: string) => {
    const newSelected = new Map(this.selectedResources);
    if (newSelected.has(resourceId)) {
      newSelected.delete(resourceId);
    } else {
      // Use the default strength from the resource definition
      const resource = this.availableResources.find(r => r.id === resourceId);
      const defaultStrength = resource?.strength ?? 1.0;
      newSelected.set(resourceId, defaultStrength);
    }
    this.selectedResources = newSelected;
    this.persistResourceChanges();
    this.refreshEstimate();
  };

  private updateResourceStrength = (resourceId: string, strength: number) => {
    const newSelected = new Map(this.selectedResources);
    if (newSelected.has(resourceId)) {
      newSelected.set(resourceId, strength);
      this.selectedResources = newSelected;
      this.persistResourceChanges();
      this.refreshEstimate();
    }
  };

  private persistResourceChanges = () => {
    // Update form state with current resources
    const resourcesArray = Array.from(this.selectedResources.entries()).map(([id, strength]) => ({
      id,
      strength
    }));
    this.form = { ...this.form, resources: resourcesArray.length > 0 ? resourcesArray : undefined };

    // Persist to localStorage
    try {
      localStorage.setItem(this.LS_KEY, JSON.stringify(this.form));
    } catch { }
  };

  // Randomize: pick one from each preset & write into inputs
  private rand<T>(arr: T[]): T { return arr[Math.floor(Math.random() * arr.length)] }
  private randomizeAll = () => {
    AppAnalytics.getPlausible()?.trackEvent('create_randomize');

    (['top', 'bottom', 'pose', 'set', 'extra', 'framing', 'viewpoint'] as const).forEach((k) => {
      const choice = this.rand(this.presets[k]);
      const el = this.querySelector<any>('#' + k);
      el?.setValue?.(choice.prompt);
      this.form = { ...this.form, [k]: choice.prompt };
    });

    // Randomize resources - select 1-3 random resources with random strengths
    if (this.availableResources.length > 0) {
      const newSelectedResources = new Map<string, number>();
      const numResources = Math.floor(Math.random() * 3) + 1; // 1-3 resources
      const shuffledResources = [...this.availableResources].sort(() => Math.random() - 0.5);

      for (let i = 0; i < Math.min(numResources, shuffledResources.length); i++) {
        const resource = shuffledResources[i];
        // Random strength between 0.5 and 1.5
        const randomStrength = Math.round((0.5 + Math.random() * 1.0) * 10) / 10;
        newSelectedResources.set(resource.id, randomStrength);
      }

      this.selectedResources = newSelectedResources;
      this.persistResourceChanges();
    }

    try { localStorage.setItem(this.LS_KEY, JSON.stringify(this.form)); } catch { }
    this.refreshEstimate();
  };

  private async enqueueJob(autogen = false) {
    if (this.creatingJob) return;
    this.error = null;
    this.nsfwBlock = null;

    // If user is logged in and using default slug, create diffsona first
    const isLoggedIn = this.authState?.user !== null && this.authState?.user !== undefined;
    if (isLoggedIn && this.slug === DEFAULT_SLUG) {
      this.creatingJob = true;
      try {
        // Get demo data from localStorage
        const demoDataRaw = localStorage.getItem(DEMO_LS_KEY);
        if (!demoDataRaw) {
          this.error = 'Please create a diffsona first from the description page';
          this.creatingJob = false;
          return;
        }

        const demoData = JSON.parse(demoDataRaw);

        // Create diffsona from description
        AppAnalytics.getPlausible()?.trackEvent('create_diffsona_from_default');
        const { slug } = await createDiffsonaFromDescription({
          name: demoData.name || 'My Diffsona',
          base: demoData.base_prompt || '',
          age: demoData.age_prompt || '',
          ethnicity: demoData.ethnicity_prompt || '',
          hair: demoData.hair_prompt || '',
          face: demoData.face_prompt || '',
          body: demoData.body_prompt || '',
        });
        this.slug = slug
      } catch (e: any) {
        console.error('Failed to create diffsona:', e);
        this.error = e?.message || 'Failed to create diffsona';
        this.creatingJob = false;
        return;
      }
    }

    if (this.estCost != null && this.balance != null && this.estCost > this.balance) {
      AppAnalytics.getPlausible()?.trackEvent('topup_modal_open', {
        props: {
          required: this.estCost - this.balance,
          balance: this.balance,
          cost: this.estCost
        },
      });
      this.topUpOpen = true;
      return;
    }

    this.creatingJob = true;

    const prompt: PromptPayload = this.currentPrompt() as PromptPayload;
    await this.enqueuePrompt(prompt);
    this.creatingJob = false;
  }

  private async pollJob(jobId: string) {
    console.log(`[POLL] job_id=${jobId} status=starting_poll`);
    this.updateJob(jobId, { status: 'running' });
    AppAnalytics.getPlausible()?.trackEvent('create_job_running');

    const pollInterval = 2000;
    let tries = 60;
    let pollCount = 0;

    while (tries-- > 0) {
      pollCount++;
      try {
        const statusRes = await fetch(`${API_ENDPOINT}/jobs/${jobId}/status`, { credentials: 'include' });
        if (statusRes.ok) {
          const data = await statusRes.json();
          console.log(`[POLL] job_id=${jobId} poll_count=${pollCount} status=${data.status} has_image=${!!data.image_url}`);

          if (data.status === 'done' && data.image_url) {
            console.log(`[POLL] job_id=${jobId} status=complete image_url=${data.image_url}`);
            AppAnalytics.getPlausible()?.trackEvent('create_job_done');

            const fullUrl = CDN_CONTENT_ENDPOINT + data.image_url;
            this.updateJob(jobId, { status: 'done', imageUrl: fullUrl });
            return;
          }
          if (data.status === 'error') {
            console.error(`[POLL] job_id=${jobId} status=error error=${data.error}`);
            AppAnalytics.getPlausible()?.trackEvent('create_job_error');

            this.updateJob(jobId, { status: 'error', error: data.error || 'Generation error' });
            return;
          }
        } else {
          console.warn(`[POLL] job_id=${jobId} poll_count=${pollCount} status_code=${statusRes.status}`);
        }
      } catch (error) {
        console.error(`[POLL] job_id=${jobId} poll_count=${pollCount} fetch_error=${error}`);
      }
      await new Promise(r => setTimeout(r, pollInterval));
    }
    console.error(`[POLL] job_id=${jobId} status=timeout poll_count=${pollCount}`);
    this.updateJob(jobId, { status: 'timeout', error: 'Timed out waiting for completion' });
    AppAnalytics.getPlausible()?.trackEvent('create_job_timeout');
  }

  private addJob(job: Job) {
    this.jobs = [job, ...this.jobs];
  }

  private summarizePrompt(prompt: PromptPayload): string {
    const parts = [
      prompt.prompt.style,
      prompt.prompt.clothing,
      prompt.prompt.pose,
      prompt.prompt.set,
    ].filter(Boolean);
    return parts.join(', ') || 'Custom generation';
  }

  private updateJob(jobId: string, patch: Partial<Job>) {
    this.jobs = this.jobs.map(j => (j.jobId === jobId ? { ...j, ...patch } : j));

    // Save anonymous demo jobs to localStorage when they complete
    if (patch.status === 'done' && patch.imageUrl) {
      const job = this.jobs.find(j => j.jobId === jobId);
      if (job?.isAnonymous) {
        this.saveDemoJobs();
      }
    }
  }

  private async pollJobStatus(jobId: string) {
    console.log(`[POLL_ANON] job_id=${jobId} status=starting_poll`);
    this.updateJob(jobId, { status: 'running' });
    AppAnalytics.getPlausible()?.trackEvent('create_job_running');

    const pollInterval = 2000;
    let tries = 60;
    let pollCount = 0;

    while (tries-- > 0) {
      pollCount++;
      try {
        // For anonymous jobs, don't send credentials
        const job = this.jobs.find(j => j.jobId === jobId);
        const isAnonymous = job?.isAnonymous || false;

        const statusRes = await fetch(`${API_ENDPOINT}/jobs/${jobId}/status`, {
          credentials: isAnonymous ? 'omit' : 'include'
        });

        if (statusRes.ok) {
          const data = await statusRes.json();
          console.log(`[POLL_ANON] job_id=${jobId} poll_count=${pollCount} is_anonymous=${isAnonymous} status=${data.status} has_image=${!!data.image_url}`);

          if (data.status === 'done' && data.image_url) {
            console.log(`[POLL_ANON] job_id=${jobId} status=complete image_url=${data.image_url}`);
            AppAnalytics.getPlausible()?.trackEvent('create_job_done');

            const fullUrl = CDN_CONTENT_ENDPOINT + data.image_url;
            this.updateJob(jobId, { status: 'done', imageUrl: fullUrl });
            return;
          }
          if (data.status === 'error') {
            console.error(`[POLL_ANON] job_id=${jobId} status=error error=${data.error}`);
            AppAnalytics.getPlausible()?.trackEvent('create_job_error');

            this.updateJob(jobId, { status: 'error', error: data.error || 'Generation error' });
            return;
          }
        } else {
          console.warn(`[POLL_ANON] job_id=${jobId} poll_count=${pollCount} status_code=${statusRes.status}`);
        }
      } catch (error) {
        console.error(`[POLL_ANON] job_id=${jobId} poll_count=${pollCount} fetch_error=${error}`);
      }
      await new Promise(r => setTimeout(r, pollInterval));
    }
    console.error(`[POLL_ANON] job_id=${jobId} status=timeout poll_count=${pollCount}`);
    this.updateJob(jobId, { status: 'timeout', error: 'Timed out waiting for completion' });
    AppAnalytics.getPlausible()?.trackEvent('create_job_timeout');
  }

  // Stripe helpers for top-up modal
  private async ensureStripe(pubKey: string) {
    if (window.Stripe) return window.Stripe(pubKey);
    await new Promise<void>((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://js.stripe.com/v3/';
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('stripe.js failed to load'));
      document.head.appendChild(s);
    });
    return window.Stripe!(pubKey);
  }

  private async buyCredits(credits: number) {
    if (this.demo) {
      // In demo, clicking "Top up" should invite signup/login rather than Stripe
      this.dispatchEvent(new CustomEvent('auth-required', { bubbles: true, composed: true, detail: { source: 'demo-topup' } }));
      AppAnalytics.getPlausible()?.trackEvent('credits_topup_demo_click');
      return;
    }

    // Check if user is logged in
    const isLoggedIn = this.authState?.user !== null && this.authState?.user !== undefined;

    console.log('[CreatePage] buyCredits called', { credits, isLoggedIn });

    if (!isLoggedIn) {
      console.log('[CreatePage] User not logged in, saving post-auth intent');

      AppAnalytics.getPlausible()?.trackEvent('credits_topup_auth_required', {
        props: { credits },
      });

      // Save post-auth intent to resume purchase after login
      const intent = { action: 'topup-credits', credits };
      savePostAuthIntent(intent);
      console.log('[CreatePage] Saved post-auth intent:', intent);

      // Close the modal
      this.topUpOpen = false;

      // Dispatch auth-required event
      this.dispatchEvent(new CustomEvent('auth-required', {
        bubbles: true,
        composed: true,
        detail: {
          source: 'topup-credits',
          message: 'Sign in to purchase credits'
        }
      }));
      return;
    }

    console.log('[CreatePage] User is logged in, proceeding with checkout');

    AppAnalytics.getPlausible()?.trackEvent('credits_topup', {
      props: { credits },
    });

    this.redirecting = true;
    try {
      // Your API should return { id, pub_key, url? }
      const { id, pub_key } = await createCheckoutSession(credits, window.location.href);

      // Prefer opening a new tab when URL is provided (keeps the create page open)
      // Fallback: standard redirect in the same tab
      const stripe = await this.ensureStripe(pub_key);
      const { error } = await stripe.redirectToCheckout({ sessionId: id });
      if (error) throw error;

      // Start a gentle balance poll so when user returns, UI updates
      if (this.balancePoll) window.clearInterval(this.balancePoll);
      this.balancePoll = window.setInterval(() => this.refreshEstimate(), 2500);
    } catch (e: any) {
      // eslint-disable-next-line no-alert
      alert(e?.message || 'Checkout failed');
      this.redirecting = false;
    }
  }

  @state() private shareModalOpen = false;
  @state() private shareModalImage: string | null = null;
  @state() private shareModalImageKey: string | null = null;

  openShare(image: string, ix: number, imageKey?: string) {
    AppAnalytics.getPlausible()?.trackEvent('create_share_open');

    this.idx = ix;
    this.shareModalImage = image;
    this.shareModalImageKey = imageKey || null;
    this.shareModalOpen = true;
  }

  closeShare() {
    this.shareModalOpen = false;
    this.shareModalImage = null;
    this.shareModalImageKey = null;
  }

  @state() private idx = 0;

  onPrev = () => {
    if (!this.jobs.length) return;
    this.idx = (this.idx - 1 + this.jobs.length) % this.jobs.length;
    this.requestUpdate();
  };

  onNext = () => {
    if (!this.jobs.length) return;
    this.idx = (this.idx + 1) % this.jobs.length;
    this.requestUpdate();
  };

  // Get demo prompts based on the preset
  private getDemoPrompts(): DiffsonaPrompts | null {
    if (!this.demo) return null;

    const baseDemoPrompts = {
      name: demoName,
      base_prompt: '',
      age_prompt: '21 year old woman',
      ethnicity_prompt: 'european',
      hair_prompt: 'shoulder-length hair',
      face_prompt: 'beautiful',
      body_prompt: 'waist, hips, legs',
    };

    // Define preset variations

    if (this.demoPreset && demoPresetValues[this.demoPreset]) {
      return { ...baseDemoPrompts, ...demoPresetValues[this.demoPreset] };
    }

    return baseDemoPrompts;
  }

  // Get demo image based on the preset
  private getDemoImage(): string | null {
    if (!this.demo) return null;

    // If no preset is specified, use the pre-initialized random demo styling
    if (!this.demoPreset) {
      return this.randomDemoStyling?.selectedDemoImage || null;
    }

    return this.demoStyling?.selectedDemoImage || null;
  }

  // Get the most recent image (either demo image or latest generated job)
  private getMostRecentImage(): { url: string; isDemoImage: boolean; isRunning?: boolean; job?: any } | null {
    // Check if there's a running job first
    const runningJobs = this.jobs.filter(j => j.status === 'queued' || j.status === 'running');
    if (runningJobs.length > 0) {
      return { url: '', isDemoImage: false, isRunning: true, job: runningJobs[0] };
    }

    // get the most recent completed job
    const completedJobs = this.jobs.filter(j => j.status === 'done' && j.imageUrl);
    if (completedJobs.length > 0) {
      return { url: completedJobs[0].imageUrl!, isDemoImage: false };
    }

    // Otherwise, if we have a demo image
    const demoImage = this.getDemoImage();
    if (demoImage) {
      return { url: demoImage, isDemoImage: true };
    }

    return null;
  }

  private makeOlderFetcher() {
    if (this.demo) {
      // Return demo images only; no network calls
      let ix = 0;
      const imgs = (this.demoImages?.length ? this.demoImages : [
      ]);
      return async (_cursor?: string) => {
        const page = { items: imgs.slice(ix, ix + 24), nextCursor: (ix + 24) < imgs.length ? String(ix + 24) : null };
        ix += 24; return page;
      };
    }

    return async (cursor?: string) => {
      const page = await getGeneratedContent(this.slug, { limit: 24, cursor });
      // Map relative → CDN URLs
      const items = (page.image_urls || []).map(u => CDN_CONTENT_ENDPOINT + u);
      return { items, nextCursor: page.next_cursor ?? null };
    }
  }

  // === UI ===
  render() {
    const pending = this.jobs.filter(j => j.status === 'queued' || j.status === 'running');

    const pill = (label: string, value?: string) => {
      if (!value) return null;
      return html`
        <div class="flex flex-col items-start gap-1 rounded-md border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-slate-200 w-full">
          <span class="text-[10px] uppercase tracking-wide text-slate-400 font-bold">${label}</span>
          <span class="max-w-full truncate">${value}</span>
        </div>
      `;
    };

    const packs = [
      {
        credits: 5000,
        bonus: 10,
        totalCredits: 5500,
        images: 275,
        blurb: 'Perfect starter pack',
        img: '/static/images/diffso-stack.webp',
        alt: 'Stack of coins',
        badge: '+10% BONUS',
      },
      {
        credits: 12000,
        bonus: 15,
        totalCredits: 13800,
        images: 690,
        blurb: 'Best for power users',
        img: '/static/images/diffso-bag.webp',
        alt: 'Bag of coins',
        badge: '+15% BONUS',
        popular: true,
      },
      {
        credits: 30000,
        bonus: 20,
        totalCredits: 36000,
        images: 1800,
        blurb: 'Maximum value bundle',
        img: '/static/images/diffso-chest.webp',
        alt: 'Chest of coins',
        badge: '+20% BONUS',
        bestValue: true,
      },
    ];

    const current = this.jobs[this.idx];

    return html`
      <main class="min-h-screen rounded-xl bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-slate-100 pb-20 md:pb-2">
        <div class="max-w-7xl mx-auto p-2">
          <current-diffsona
            .slug=${this.slug}
            .demo=${this.demo}
            .prompts=${this.demoDiffsonaPrompts}
          ></current-diffsona>

          <section class="mt-2 rounded-2xl border border-white/10 bg-white/5 from-slate-900/70 to-slate-950/80 p-2 sm:p-5">
            ${this.activeTab === 'images' ? html`
              ${(() => {
          const recentImage = this.getMostRecentImage();
          if (!recentImage) return null;

          // Show running job placeholder if there's a running job
          if (recentImage.isRunning && recentImage.job) {
            return html`
                      <section class="mb-2">
                        <div class="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60 max-w-md mx-auto ${recentImage.job.isAnonymous ? 'border-indigo-500/30' : ''}">
                          <div class="h-96 w-full rounded-lg bg-slate-800/80 animate-pulse"></div>
                          <div class="absolute inset-0 flex flex-col items-center justify-center space-y-2">
                            <div class="text-slate-300 text-sm">Generating...</div>
                            <div class="text-slate-400/90 text-xs">Runtime: ${this.formatDuration(recentImage.job.elapsedSec)}</div>
                            <div class="text-[10px] text-slate-500 uppercase tracking-wide">${recentImage.job.status}</div>
                            ${recentImage.job.isAnonymous ? html`
                              <div class="text-[10px] text-indigo-300/80 mt-1">
                                💫 Sign up for unlimited access
                              </div>
                            ` : ''}
                          </div>
                        </div>
                      </section>
                    `;
          }

          // Show completed or demo image
          return html`
                      <section class="mb-2">
                        <div class="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60 max-w-md mx-auto cursor-pointer"
                          @click=${() => {
              if (!recentImage.isDemoImage) {
                // Find the job index for the image
                const jobIndex = this.jobs.findIndex(j => j.imageUrl === recentImage.url);
                if (jobIndex >= 0) {
                  this.openShare(recentImage.url, jobIndex);
                }
              }
            }}
                        >
                          <img
                            src="${recentImage.url}"
                            class="w-full h-96 object-contain bg-black/20"
                            loading="lazy"
                            alt="${recentImage.isDemoImage ? 'Demo image' : 'Most recent generation'}"
                          />
                          ${recentImage.isDemoImage ? html`
                            <div class="absolute top-2 right-2 px-2 py-1 bg-indigo-500/80 text-white text-xs rounded-lg">
                              Demo
                            </div>
                          ` : null}
                        </div>
                      </section>
                    `;
        })()}

              <!-- Character Selector -->
              <section class="mt-2 hidden">
                <h2 class="text-lg font-semibold text-slate-100 mb-4">Character Style</h2>
                ${this.resourcesLoading ? html`
                  <div class="flex items-center justify-center py-8">
                    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
                  </div>
                ` : html`
                  <div class="relative">
                    <div class="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-track-slate-800 scrollbar-thumb-slate-600">
                      ${this.availableResources.map(resource => {
          const isSelected = this.selectedResources.has(resource.id);
          const currentStrength = this.selectedResources.get(resource.id) ?? resource.strength;
          return html`
                          <div class="flex-shrink-0">
                            <div class="relative overflow-hidden rounded-xl border-2 transition-all duration-200 w-48 h-72 group
                                       ${isSelected
              ? 'border-indigo-400 bg-indigo-500/20 ring-2 ring-indigo-400/50'
              : 'border-white/10 bg-slate-800/50 hover:border-white/20 hover:bg-slate-700/50'}">
                              <button
                                class="w-full h-48 block"
                                @click=${() => this.toggleResource(resource.id)}
                              >
                                <img
                                  src="${resource.sample_image}"
                                  alt="${resource.display_name}"
                                  class="w-full h-40 object-cover"
                                  loading="lazy"
                                  @error=${(e: any) => {
              e.target.src = '/static/images/default-avatar.webp';
            }}
                                />
                                <div class="p-3 h-8 flex items-center justify-between">
                                  <div class="flex-1">
                                    <h3 class="text-sm font-semibold text-white text-left line-clamp-1">
                                      ${resource.display_name}
                                    </h3>
                                  </div>
                                  ${isSelected ? html`
                                    <div class="flex items-center justify-center w-6 h-6 rounded-full bg-indigo-500">
                                      <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                                      </svg>
                                    </div>
                                  ` : null}
                                </div>
                              </button>

                              <div class="p-3 pt-0">
                                <p class="text-xs text-slate-300 line-clamp-2 mb-2">
                                  ${resource.description}
                                </p>

                                ${isSelected ? html`
                                  <div class="space-y-2">
                                    <div class="flex items-center justify-between text-xs">
                                      <span class="text-slate-400">Strength:</span>
                                      <span class="text-white font-mono">${currentStrength.toFixed(1)}</span>
                                    </div>
                                    <input
                                      type="range"
                                      min="0"
                                      max="2"
                                      step="0.1"
                                      .value=${currentStrength.toString()}
                                      class="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer slider"
                                      @input=${(e: Event) => {
                const target = e.target as HTMLInputElement;
                this.updateResourceStrength(resource.id, parseFloat(target.value));
              }}
                                      @click=${(e: Event) => e.stopPropagation()}
                                    />
                                    <div class="flex justify-between text-xs text-slate-500">
                                      <span>0</span>
                                      <span>1</span>
                                      <span>2</span>
                                    </div>
                                  </div>
                                ` : html`
                                  <div class="text-xs text-slate-400">
                                    Default: ${resource.strength}
                                  </div>
                                `}
                              </div>

                              ${isSelected ? html`
                                <div class="absolute inset-0 bg-indigo-500/10 pointer-events-none"></div>
                              ` : null}
                            </div>
                          </div>
                        `;
        })}
                    </div>
                    ${this.selectedResources.size > 0 ? html`
                      <div class="mt-3 flex items-center gap-2 text-sm text-slate-300">
                        <span>Selected styles:</span>
                        <div class="flex flex-wrap gap-1">
                          ${Array.from(this.selectedResources.entries()).map(([id, strength]) => {
          const resource = this.availableResources.find(r => r.id === id);
          return resource ? html`
                              <span class="inline-flex items-center gap-1 px-2 py-1 bg-indigo-500/20 border border-indigo-400/30 rounded-md text-xs">
                                ${resource.display_name} <span class="font-mono text-slate-400">(${strength.toFixed(1)})</span>
                                <button
                                  class="hover:text-red-300 ml-1"
                                  @click=${() => this.toggleResource(id)}
                                >×</button>
                              </span>
                            ` : null;
        })}
                        </div>
                        <button
                          class="text-xs text-slate-400 hover:text-slate-300 underline ml-2"
                          @click=${() => {
              this.selectedResources = new Map();
              this.persistResourceChanges();
              this.refreshEstimate();
            }}
                        >Clear all</button>
                      </div>
                    ` : null}
                  </div>
                `}
              </section>

              <!-- Builder -->
              <collapsible-section
                title="Outfit & Scene"
                icon="🎨"
                description="Clothing, pose, scene details"
                .open=${true}
              >
                <section class="flex overflow-x-auto gap-2">
                  ${this.section('Top', 'top', this.presets.top, true, this.form.top)}
                  ${this.section('Bottoms', 'bottom', this.presets.bottom, true, this.form.bottom)}
                  ${this.section('Pose', 'pose', this.presets.pose, true, this.form.pose)}
                  ${this.section('Set', 'set', this.presets.set, true, this.form.set)}
                  ${this.section('Framing', 'framing', this.presets.framing, false, this.form.framing || DEFAULT_FRAMING)}
                  ${this.section('Viewpoint', 'viewpoint', this.presets.viewpoint, false, this.form.viewpoint || DEFAULT_VIEWPOINT)}
                  ${this.section('Extra', 'extra', this.presets.extra, true, this.form.extra)}
                </section>
              </collapsible-section>

              <section class="mt-2 hidden">
                <div class="rounded-2xl border border-white/10 bg-white/5 p-3">
                  <div class="text-sm text-slate-300 mb-2">Your look</div>
                  <div class="flex flex-wrap gap-2">
                    ${pill('Clothing', [this.form.top, this.form.bottom].filter(Boolean).join(', '))}
                    ${pill('Pose', this.form.pose)}
                    ${pill('Set', this.form.set)}
                    ${pill('Framing', this.form.framing || DEFAULT_FRAMING)}
                    ${pill('Viewpoint', this.form.viewpoint || DEFAULT_VIEWPOINT)}
                    ${pill('Style', this.form.extra)}
                  </div>
                </div>
              </section>

              ${this.error ? html`
                <p class="mt-4 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">${this.error}</p>
              ` : null}

              ${this.nsfwBlock ? html`
                <div class="mt-2 rounded-2xl border border-amber-400/30 bg-amber-500/10">
                  <div class="p-4 flex items-start gap-3">
                    <div class="h-6 w-6 grid place-items-center rounded-md border border-amber-400/30 bg-amber-500/20 text-amber-200 text-sm">!</div>
                    <div class="flex-1">
                      <div class="text-sm font-semibold text-amber-200">🥵️🔥 Too Hot To Handle!</div>
                      <div class="text-sm text-amber-100">Try <a href="https://diffsonax.com" target="_blank" class="link-secondary">diffsonax.com</a> for NSFW Diffsonas</div>
                      <div class="mt-3 flex">
                        <a href="https://diffsonax.com" target="_blank" class="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-2 text-xs flex items-baseline gap-1 justify-self-start">
                          Try It
                          <svg xmlns="http://www.w3.org/2000/svg" width="12px" height="12px" viewBox="0 0 20 20" fill="none">
                            <path fill="#FFFFFF" fill-rule="evenodd" d="M18.885 2a1 1 0 00-1-1h-6a1 1 0 100 2h3.586L9.178 9.293a1 1 0 101.414 1.414l6.293-6.293V8a1 1 0 102 0V2zM3.009 3a2.012 2.012 0 00-1.998 2.218c.148 1.453.374 3.978.374 5.782 0 1.746-.212 4.17-.36 5.642a2.028 2.028 0 002.218 2.218c1.473-.148 3.896-.36 5.642-.36 1.804 0 4.33.226 5.782.374a2.012 2.012 0 002.218-1.998V12a1 1 0 10-2 0v4.878l-.003.003a.018.018 0 01-.006.003h-.006c-1.451-.147-4.068-.384-5.985-.384-1.857 0-4.37.222-5.842.37h-.008a.034.034 0 01-.012-.008.033.033 0 01-.008-.012.01.01 0 010-.002v-.006c.148-1.473.37-3.985.37-5.842 0-1.917-.237-4.534-.385-5.985v-.006l.004-.006A.016.016 0 013.007 5h4.878a1 1 0 000-2H3.009z"/>
                          </svg>
                        </a>
                        <button class="underline px-3 py-2 text-xs"
                          @click=${() => (this.nsfwBlock = null)}>Dismiss</button>
                      </div>
                    </div>
                  </div>
                </div>
              ` : null}

              <!-- Primary actions -->
              <div class="mt-2 hidden md:flex flex-wrap items-center gap-3 justify-center">
                <button
                  class="generate-btn relative inline-flex items-center gap-2 rounded-2xl
                         bg-gradient-to-r from-indigo-500/50 via-purple-500/40 to-indigo-500/50
                         hover:from-indigo-500/60 hover:via-purple-500/50 hover:to-indigo-500/60
                         px-6 py-4 font-bold text-white disabled:opacity-50 cursor-pointer w-full justify-between
                         shadow-[0_0_20px_rgba(99,102,241,0.3),0_0_40px_rgba(168,85,247,0.2)]
                         hover:shadow-[0_0_30px_rgba(99,102,241,0.5),0_0_60px_rgba(168,85,247,0.3)]
                         animate-[pulse_3s_ease-in-out_infinite]
                         transition-all duration-500 ease-out
                         hover:scale-[1.02]
                         group overflow-hidden
                         max-w-xl"
                  @click=${(e: MouseEvent) => {
                    const btn = e.currentTarget as HTMLElement;
                    btn.style.transform = 'scale(0.95) rotate(2deg)';
                    btn.style.boxShadow = '0 0 30px rgba(99, 102, 241, 0.6), 0 0 60px rgba(168, 85, 247, 0.4)';
                    this.createCoinSplash(e);
                    setTimeout(() => {
                      btn.style.transform = 'scale(1.05) rotate(-1deg)';
                      setTimeout(() => {
                        btn.style.transform = '';
                        btn.style.boxShadow = '';
                      }, 200);
                    }, 150);
                    this.enqueueJob();
                  }}
                  ?disabled=${this.creatingJob}
                  title="Generate your diffsona image"
                >
                  <!-- Animated background shimmer -->
                  <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent
                             translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1500 ease-out"></div>

                  <!-- Subtle sparkle effects -->
                  <div class="absolute top-2 left-6 w-1 h-1 bg-white/60 rounded-full animate-pulse opacity-40"></div>
                  <div class="absolute top-4 right-10 w-1 h-1 bg-indigo-200/50 rounded-full animate-pulse opacity-30" style="animation-delay: 0.7s"></div>
                  <div class="absolute bottom-3 left-16 w-1 h-1 bg-purple-200/50 rounded-full animate-pulse opacity-30" style="animation-delay: 1.4s"></div>

                  ${this.creatingJob
          ? html`
                    <span class="relative z-10 flex items-center gap-2">
                      <div class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Generating magic...
                    </span>
                  `
          : (this.estCost != null ? html`
                  <span class="relative z-10 text-base font-bold tracking-wide group-hover:scale-105 transition-transform duration-200
                               drop-shadow-[0_0_8px_rgba(255,255,255,0.5)] group-hover:drop-shadow-[0_0_12px_rgba(255,255,255,0.7)]">
                    ✨ Generate
                  </span>
                  ${this.estCost > 0 ? html`
                    <span class="relative z-10 flex items-center gap-1.5 py-1 px-2 rounded-lg
                               bg-gradient-to-r from-amber-500/30 to-yellow-500/30
                               text-amber-100 font-semibold text-sm
                               shadow-[0_0_12px_rgba(251,191,36,0.4)]
                               group-hover:shadow-[0_0_20px_rgba(251,191,36,0.6)]
                               group-hover:from-amber-400/40 group-hover:to-yellow-400/40
                               group-hover:scale-105 transition-all duration-200">
                      <span class="text-sm">${this.estCost}</span>
                      <img src="/static/images/diffso-coin-64.webp" alt="Credits" class="h-5 w-5" />
                    </span>
                  ` : null}
                  ` : html`
                  <span class="relative z-10 text-base font-bold tracking-wide group-hover:scale-105 transition-transform duration-200
                               drop-shadow-[0_0_8px_rgba(255,255,255,0.5)] group-hover:drop-shadow-[0_0_12px_rgba(255,255,255,0.7)]">
                    ✨ Generate
                  </span>
                  `)}
                </button>

                <button
                  class="hidden rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2 text-sm"
                  @click=${this.randomizeAll}
                  title="Pick a random preset for each field"
                >🎲 Randomize</button>

                  <!--<button
                    class="rounded-xl border border-emerald-400/30 bg-emerald-500/20 hover:bg-emerald-500/30 px-4 py-2 text-sm"
                    @click=${() => (this.topUpOpen = true)}
                  >
                    Top up credits
                  </button>-->
                ${this.estCost != null && this.balance != null && this.estCost > this.balance ? html`
                  <span class="text-xs text-amber-300">You need ${this.estCost - this.balance} more</span>
                ` : null}
              </div>

              <!-- Session results (excluding most recent) -->
              <section class="">
                <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-2">
                  ${(() => {
          // Filter running jobs, but exclude the first one if it's shown in recentImage display
          const runningJobs = this.jobs.filter(j => j.status === 'queued' || j.status === 'running');
          const recentImage = this.getMostRecentImage();
          const jobsToShow = recentImage?.isRunning && runningJobs.length > 0
            ? runningJobs.slice(1)
            : runningJobs;

          return jobsToShow.map(j => html`
                    <div class="rounded-xl border border-white/10 bg-slate-900/40 p-2 ${j.isAnonymous ? 'border-indigo-500/30' : ''}">
                      <div class="h-44 w-full rounded-lg bg-slate-800/80 animate-pulse"></div>
                      <div class="mt-2 space-y-1.5 text-[11px]">
                        <div class="text-slate-400/90">Runtime: ${this.formatDuration(j.elapsedSec)}</div>
                        <div class="text-[10px] text-slate-500 uppercase tracking-wide">${j.status}</div>
                        ${j.isAnonymous ? html`
                          <div class="text-[10px] text-indigo-300/80 mt-1">
                            💫 Sign up for unlimited access
                          </div>
                        ` : ''}
                      </div>
                    </div>
                  `);
        })()}
                  ${(() => {
          // Get completed jobs, excluding the most recent one that's shown above
          const completedJobs = this.jobs.filter(j => j.status === 'done' && j.imageUrl);
          const recentImage = this.getMostRecentImage();

          // Skip the first completed job if it's being shown as the most recent image
          // BUT don't skip it if we're showing a running job in the recentImage display
          const jobsToShow = recentImage && !recentImage.isDemoImage && !recentImage.isRunning && completedJobs.length > 0
            ? completedJobs.slice(1)
            : completedJobs;

          return jobsToShow.map((j, ix) => html`
                      <figure class="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60 cursor-pointer group"
                        @click=${() => {
              if (j.isAnonymous) {
                // Show auth modal for anonymous jobs
                this.dispatchEvent(new CustomEvent('auth-required', {
                  bubbles: true,
                  composed: true,
                  detail: {
                    source: 'anonymous-image-view',
                    message: 'Sign up to view full image and claim 200 free credits!'
                  }
                }));
              } else {
                // Calculate the correct index in the original jobs array
                const originalIndex = this.jobs.findIndex(job => job === j);
                this.openShare(j.imageUrl!, originalIndex);
              }
            }}
                      >
                        <img src=${j.imageUrl!}
                             class="w-full h-72 md:h-96 object-contain bg-black/20 "
                             loading="lazy" alt="Generated" />
                      </figure>
                    `);
        })()}
                  ${this.jobs.filter(j => j.status === 'error' || j.status === 'timeout').map(j => html`
                    <div class="rounded-xl border border-red-500/30 bg-red-500/10 p-2">
                      <div class="h-44 w-full rounded-lg bg-red-900/30 grid place-items-center text-red-300 text-xs">Failed</div>
                      <div class="mt-1 flex gap-2">
                        <button
                          class="rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 px-2 py-1 text-[11px] cursor-pointer"
                          @click=${() => this.enqueuePrompt(j.prompt)}
                        >Retry</button>
                      </div>
                    </div>
                  `)}
                </div>
              </section>
            ` : html`
              <diffsona-scenarios .slug=${this.slug}></diffsona-scenarios>
            `}
          </section>

          <!-- Finished: session items first; infinite-scroll older afterward -->
          <infinite-image-grid
            .fetchPage=${this.makeOlderFetcher()}
            .autoLoad=${true}
            .shareCta=${true}
            @open=${(e: CustomEvent) => this.openShare(e.detail.url)}
          ></infinite-image-grid>
        </div>
      </main>

      <!-- Top up credits modal -->
      ${this.topUpOpen ? html`
        <div class="fixed inset-0 z-50">
          <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click=${() => {
            AppAnalytics.getPlausible()?.trackEvent('topup_modal_dismiss');
            this.topUpOpen = false;
          }}></div>
          <div class="absolute inset-0 grid place-items-center p-4 overflow-y-auto">
            <div class="w-full max-w-4xl rounded-2xl border border-white/10 bg-slate-900/95 my-auto">
              <div class="flex items-center justify-between px-4 py-3 border-b border-white/10 sticky top-0 bg-slate-900/95 z-10 backdrop-blur-sm">
                <h3 class="text-sm font-semibold flex items-center gap-2">
                  Top up <img src="/static/images/diffso-coin-64.webp" alt="Diffso Icon" class="h-6 w-6" />
                </h3>
                <button class="text-slate-300 hover:text-white" @click=${() => {
                  AppAnalytics.getPlausible()?.trackEvent('topup_modal_dismiss');
                  this.topUpOpen = false;
                }}>✕</button>
              </div>
              <div class="p-4 space-y-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
                <p class="text-sm text-slate-300">
                  You need <b>${Math.max(0, (this.estCost ?? 0) - (this.balance ?? 0))}</b> more credits to generate this look.
                </p>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  ${packs.map(p => html`
                    <button
                      class="
                        group relative overflow-hidden rounded-2xl
                        border border-indigo-400/30
                        bg-gradient-to-br from-slate-900/90 via-indigo-950/30 to-slate-950/90
                        backdrop-blur
                        shadow-lg shadow-indigo-500/10
                        hover:shadow-xl hover:shadow-indigo-500/20
                        hover:border-indigo-400/50
                        hover:scale-[1.02] active:scale-[0.98]
                        transition-all duration-300 ease-out
                        text-left disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-indigo-400/40
                        w-full cursor-pointer
                      "
                      @click=${() => {
                        AppAnalytics.getPlausible()?.trackEvent('topup_pack_select', {
                          props: { credits: p.credits, totalCredits: p.totalCredits },
                        });
                        this.buyCredits(p.credits);
                      }}
                      ?disabled=${this.redirecting}
                    >
                      <!-- Animated background shimmer on hover -->
                      <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent
                                 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-out"></div>

                      <!-- Badge indicators -->
                      <div class="absolute top-3 right-3 z-10 flex flex-col gap-2 items-end">
                        ${p.badge ? html`
                          <div class="rounded-full border border-emerald-400/60 bg-emerald-500/40 px-2.5 py-1 text-[11px] text-emerald-50 font-bold
                                     shadow-lg shadow-emerald-500/30
                                     group-hover:border-emerald-400/80 group-hover:bg-emerald-500/50 group-hover:scale-105 transition-all duration-300">
                            ${p.badge}
                          </div>
                        ` : null}
                        ${p.popular ? html`
                          <div class="rounded-full border border-amber-400/60 bg-amber-500/40 px-2.5 py-1 text-[11px] text-amber-50 font-bold
                                     shadow-lg shadow-amber-500/30 transition-all duration-300">
                            ⭐ Most Popular
                          </div>
                        ` : null}
                        ${p.bestValue ? html`
                          <div class="rounded-full border border-purple-400/60 bg-purple-500/40 px-2.5 py-1 text-[11px] text-purple-50 font-bold
                                     shadow-lg shadow-purple-500/30 transition-all duration-300">
                            💎 Best Value
                          </div>
                        ` : null}
                      </div>

                      <!-- Hero image -->
                      <div class="relative">
                        <div class="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-300"
                             style="background: radial-gradient(60% 60% at 50% 0%, rgba(99,102,241,.35) 0%, rgba(14,165,233,.25) 35%, transparent 70%);">
                        </div>
                        <div class="w-full aspect-[16/10] grid place-items-center">
                          <img
                            src=${p.img}
                            alt=${p.alt}
                            class="h-24 md:h-32 object-contain drop-shadow-[0_6px_24px_rgba(0,0,0,0.45)] group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                      </div>

                      <!-- Copy -->
                      <div class="px-4 pb-4 relative z-10">
                        <div class="flex items-center gap-2 mb-2">
                          <div class="text-2xl font-black text-white">${p.totalCredits.toLocaleString()}</div>
                          <img src="/static/images/diffso-coin-64.webp" alt="Credits" class="h-8 w-8" />
                        </div>

                        <div class="space-y-2 mb-3">
                          <div class="flex items-center gap-2 text-sm">
                            <span class="text-slate-400">Generate over</span>
                            <span class="text-indigo-300 font-bold text-lg">${p.images} images</span>
                          </div>
                          <div class="text-slate-400 text-sm">${p.blurb}</div>
                        </div>

                        <div class="mt-3">
                          <span class="inline-flex items-center justify-center gap-2 rounded-xl w-full
                                     border border-indigo-400/40
                                     bg-gradient-to-r from-indigo-500/20 to-purple-500/20
                                     hover:from-indigo-500/30 hover:to-purple-500/30
                                     px-3 py-2 text-sm font-bold text-white
                                     group-hover:border-indigo-400/60
                                     group-hover:shadow-lg group-hover:shadow-indigo-500/30
                                     transition-all duration-300">
                            Get ${p.totalCredits.toLocaleString()}
                            <img src="/static/images/diffso-coin-64.webp" alt="Credits" class="h-5 w-5" />
                          </span>
                        </div>
                      </div>
                    </button>
                  `)}
                </div>

                <div class="text-xs text-slate-500 text-center">
                  💳 Secure payments powered by Stripe • ⚡ Instant delivery
                </div>
              </div>
            </div>
          </div>
        </div>
      ` : null}

      <share-modal
        .open=${this.shareModalOpen}
        .image=${current?.imageUrl ?? this.shareModalImage}
        .imageKey=${current?.imageUrl?.replace(`${CDN_CONTENT_ENDPOINT}/`, '') ?? this.shareModalImageKey}
        .showNav=${true}
        @close=${this.closeShare}
        @prev=${this.onPrev}
        @next=${this.onNext}
      ></share-modal>

      <!-- Mobile floating Generate button -->
      <div class="fixed bottom-16 left-4 right-4 z-10 md:hidden">
        <button
          class="generate-btn-mobile relative inline-flex items-center gap-2 rounded-2xl
                 bg-gradient-to-r from-indigo-500/50 via-purple-500/40 to-indigo-500/50
                 hover:from-indigo-500/60 hover:via-purple-500/50 hover:to-indigo-500/60
                 px-6 py-4 font-bold text-white disabled:opacity-50 cursor-pointer w-full justify-center
                 shadow-[0_0_20px_rgba(99,102,241,0.3),0_0_40px_rgba(168,85,247,0.2)]
                 hover:shadow-[0_0_30px_rgba(99,102,241,0.5),0_0_60px_rgba(168,85,247,0.3)]
                 animate-[pulse_3s_ease-in-out_infinite]
                 transition-all duration-500 ease-out
                 hover:scale-[1.02]
                 group overflow-hidden"
          @click=${(e: MouseEvent | TouchEvent) => {
            const btn = e.currentTarget as HTMLElement;
            btn.style.transform = 'scale(0.95) rotate(2deg)';
            btn.style.boxShadow = '0 0 30px rgba(99, 102, 241, 0.6), 0 0 60px rgba(168, 85, 247, 0.4)';
            this.createCoinSplash(e);
            setTimeout(() => {
              btn.style.transform = 'scale(1.05) rotate(-1deg)';
              setTimeout(() => {
                btn.style.transform = '';
                btn.style.boxShadow = '';
              }, 200);
            }, 150);
            this.enqueueJob();
          }}
          ?disabled=${this.creatingJob}
          title="Generate your diffsona image"
        >
          <!-- Animated background shimmer -->
          <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent
                     translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1500 ease-out"></div>

          <!-- Subtle sparkle effects -->
          <div class="absolute top-2 left-6 w-1 h-1 bg-white/60 rounded-full animate-pulse opacity-40"></div>
          <div class="absolute top-4 right-10 w-1 h-1 bg-indigo-200/50 rounded-full animate-pulse opacity-30" style="animation-delay: 0.7s"></div>
          <div class="absolute bottom-3 left-16 w-1 h-1 bg-purple-200/50 rounded-full animate-pulse opacity-30" style="animation-delay: 1.4s"></div>

          ${this.creatingJob
        ? html`
              <span class="relative z-10 flex items-center gap-2">
                <div class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Generating magic...
              </span>
            `
        : (this.estCost != null ? html`
              <span class="relative z-10 text-base font-bold tracking-wide group-hover:scale-105 transition-transform duration-200
                           drop-shadow-[0_0_8px_rgba(255,255,255,0.5)] group-hover:drop-shadow-[0_0_12px_rgba(255,255,255,0.7)]">
                ✨ Generate
              </span>
              ${this.estCost > 0 ? html`
                <span class="relative z-10 flex items-center gap-1.5 py-1 px-2 rounded-lg
                         bg-gradient-to-r from-amber-500/30 to-yellow-500/30
                         text-amber-100 font-semibold text-sm
                         shadow-[0_0_12px_rgba(251,191,36,0.4)]
                         group-hover:shadow-[0_0_20px_rgba(251,191,36,0.6)]
                         group-hover:from-amber-400/40 group-hover:to-yellow-400/40
                         group-hover:scale-105 transition-all duration-200">
                  <span class="text-sm">${this.estCost}</span>
                  <img src="/static/images/diffso-coin-64.webp" alt="Credits" class="h-5 w-5" />
                </span>
              `: null}
            ` : html`
              <span class="relative z-10 text-base font-bold tracking-wide group-hover:scale-105 transition-transform duration-200
                           drop-shadow-[0_0_8px_rgba(255,255,255,0.5)] group-hover:drop-shadow-[0_0_12px_rgba(255,255,255,0.7)]">
                ✨ Generate ✨
              </span>
            `)}
        </button>
      </div>
    `;
  }

  private section(label: string, id: keyof FormState, values: Choice[], allowCustom: boolean, initial: string) {
    const fieldKey = id as FormFieldKey;
    const info = FIELD_INFO[fieldKey];

    return html`
      <div class="flex flex-col items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2.5 py-2 text-xs text-slate-200 max-w-full mb-2">
        <div class="flex items-center justify-center gap-1.5 w-full">
          <span class="text-lg font-bold tracking-wide text-slate-400">${label}</span>
          <info-tooltip
            .description=${info.description}
            .examples=${info.examples}
          ></info-tooltip>
        </div>
        <prompt-input
          id=${id}
          .label=${id}
          .values=${values}
          ?allow-custom=${allowCustom}
          .initialValue=${initial}
        ></prompt-input>
      </div>
    `;
  }
}

